import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Plus, Minus, ArrowLeft, Package, Cpu, Puzzle, Info } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useQuote } from "@/context/QuoteContext";
import { UYGUNLUK } from "@/lib/utils-tr";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { productPath, categoryPath, breadcrumbLd, HIDDEN_BRANDS, SITE_ORIGIN } from "@/lib/seo";

export default function ProductDetailPage() {
  const { urun_id } = useParams();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState(1);
  const { addItem } = useQuote();
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.get(`/catalog/products/${urun_id}`).then((r) => {
      if (r.data.yonlendir) { navigate(r.data.yonlendir, { replace: true }); return; }
      setD(r.data);
      setSelected(r.data.product.urun_id);
      setQty(1);
      const slug = r.data.product.seo_slug;
      if (slug && urun_id !== slug) window.history.replaceState(window.history.state, "", `/urun/${slug}`);
    }).catch(() => setD(null)).finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [urun_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="text-center py-24 text-slate-400">Yükleniyor...</div>;
  if (!d) return <div className="text-center py-24 text-slate-400"><Seo title="Ürün bulunamadı | Molar Kimya" noindex status={404} />Ürün bulunamadı.</div>;

  const variants = d.variants.length > 1 ? d.variants : [];
  // Seçili ambalajın kendi kaydı: kod, ambalaj ve açıklamalar birlikte değişir
  const p = variants.find((v) => v.urun_id === selected) || d.product;
  const selVariant = p;

  const selectVariant = (id) => {
    setSelected(id);
    const v = variants.find((x) => x.urun_id === id);
    window.history.replaceState(window.history.state, "", productPath(v || { urun_id: id }));
  };

  const crumbItems = [{ name: "Ürünler", path: "/urunler" },
    ...(d.kategori_breadcrumb || []).map((c) => ({ name: c.kategori_adi, path: categoryPath(c) })),
    { name: p.urun_adi_tr, path: productPath(p) }];
  const brand = p.marka && !HIDDEN_BRANDS.includes(p.marka) ? p.marka : null;
  const productLd = {
    "@context": "https://schema.org", "@type": "Product",
    name: p.urun_adi_tr, sku: p.urun_kodu, url: SITE_ORIGIN + productPath(p),
    description: p.kisa_aciklama || p.seo_description || undefined,
    category: [p.ana_kategori, p.alt_kategori].filter(Boolean).join(" > ") || undefined,
    ...(brand ? { brand: { "@type": "Brand", name: brand } } : {}),
    ...(p.diger_isimler ? { alternateName: p.diger_isimler } : {}),
    additionalProperty: [["CAS No", p.cas_no], ["Ambalaj", p.ambalaj], ["E-Kodu", p.e_kodu_bilgisi]]
      .filter(([, v]) => v).map(([n, v]) => ({ "@type": "PropertyValue", name: n, value: v })),
  };

  const add = () => {
    addItem(p, qty);
    toast.success(`${p.urun_kodu} · ${p.ambalaj || ""} teklif listesine eklendi`);
  };

  const specs = [
    ["Ürün Kodu", p.urun_kodu], ["Tedarikçi Markası / Marka", p.marka], ["Kategori", p.ana_kategori],
    ["Alt Kategori", p.alt_kategori], ["Ürün Grubu", p.urun_grubu], ["Kalite", p.kalite],
    ["Model", p.model], ["CAS No", p.cas_no], ["E-Kodu", p.e_kodu_bilgisi],
    ["Fiziksel Form", p.fiziksel_form], ["Aroma Bazı", p.aroma_bazi], ["Aroma Profili", p.aroma_profili],
    ["Renk Aralığı", p.renk_araligi], ["Pigment Grubu", p.pigment_grubu],
    ["Ambalaj", p.ambalaj], ["Satış Birimi", p.satis_birimi], ["Test Sayısı", p.test_sayisi],
  ].filter(([, v]) => v !== null && v !== undefined && v !== "");

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
      <Seo title={p.seo_title || `${p.urun_adi_tr} | Molar Kimya`}
        description={p.seo_description || p.kisa_aciklama}
        path={productPath(p)} noindex={p.indeksle === false || !!p.satista_degil} jsonLd={[productLd, breadcrumbLd(crumbItems)]} />
      {p.satista_degil && <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800" data-testid="not-for-sale">Bu ürün şu an satışta değil.</div>}
      <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-teal-600 flex items-center gap-1 mb-2"
        data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Geri
      </button>

      {/* Tıklanabilir kategori yolu */}
      {d.kategori_breadcrumb?.length > 0 && (
        <nav className="flex items-center flex-wrap gap-1 text-sm mb-4" data-testid="detail-breadcrumb">
          <Link to="/urunler" className="text-slate-500 hover:text-teal-600">Tüm Ürünler</Link>
          {d.kategori_breadcrumb.map((c) => (
            <span key={c.kategori_id} className="flex items-center gap-1">
              <span className="text-slate-300">›</span>
              <Link to={categoryPath(c)} data-testid={`detail-crumb-${c.kategori_id}`}
                className="text-slate-500 hover:text-teal-600">
                {c.kategori_adi}
              </Link>
            </span>
          ))}
        </nav>
      )}

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <Badge variant="outline" className="font-mono text-xs" data-testid="detail-urun-kodu" data-nosnippet>Ürün kodu: {p.urun_kodu}</Badge>
              {p.ambalaj && <Badge variant="secondary" className="text-xs" data-testid="detail-ambalaj">{p.ambalaj}</Badge>}
              <span className="text-sm text-teal-700 font-medium">
                <span className="text-slate-500 font-normal">Tedarikçi Markası / Marka:</span> {p.marka}
              </span>
            </div>
            <h1 className="font-head text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">{p.urun_adi_tr}</h1>
            {p.urun_adi_en && <p className="text-slate-400 mt-1 text-sm">{p.urun_adi_en}</p>}
            {p.kisa_aciklama && <p className="text-slate-600 mt-4 leading-relaxed" data-testid="detail-kisa-aciklama">{p.kisa_aciklama}</p>}
            {p.gorsel_url && <img src={p.gorsel_url} alt={p.urun_adi_tr} className="mt-4 max-h-72 rounded-lg border object-contain bg-white" data-testid="detail-image" />}
            <p className="mt-4 text-sm text-slate-600" data-testid="detail-price-note">Fiyat ve tedarik koşulları, talep edilen ürün ve miktara göre teklif aşamasında belirlenir.</p>
            {(p.teknik_dokuman_url || p.analiz_belgesi_url || p.guvenlik_dokuman_url) && <div className="flex flex-wrap gap-3 mt-4 text-sm" data-testid="detail-docs">
              {[["Teknik doküman", p.teknik_dokuman_url], ["Analiz belgesi", p.analiz_belgesi_url], ["Güvenlik dokümanı", p.guvenlik_dokuman_url]].filter(([, u]) => u).map(([l, u]) => <a key={l} href={u} target="_blank" rel="noreferrer" className="text-teal-700 underline">{l}</a>)}</div>}
            {p.urun_kullanim_uyarisi && <p className="mt-4 text-sm bg-slate-50 border rounded-lg p-3 whitespace-pre-line" data-testid="detail-warning">{p.urun_kullanim_uyarisi}</p>}
          </div>

          {(p.satis_uygunlugu === "kurum" || p.satis_uygunlugu === "inceleme") && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3" data-testid="eligibility-notice">
              <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                Bu ürünün tedariki firma veya kurum bilgisi ve kullanım amacı doğrulaması gerektirebilir.
                Bir kuruluş adına araştırma yapıyorsanız ilgili seçeneği işaretleyerek talebinizi iletebilirsiniz.
              </p>
            </div>
          )}

          {/* Technical details — preserve multi-line */}
          {(p.urun_detay_aciklamasi || p.kaynak_teknik_tanim) && (
            <section>
              <h2 className="font-head text-lg font-semibold text-slate-900 mb-3">Teknik Açıklama</h2>
              <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-line bg-white border rounded-lg p-4"
                data-testid="technical-description">
                {p.urun_detay_aciklamasi || p.kaynak_teknik_tanim}
              </div>
            </section>
          )}

          {p.teknik_ozellikler && (
            <section>
              <h2 className="font-head text-lg font-semibold text-slate-900 mb-3">Teknik Özellikler</h2>
              <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-line bg-white border rounded-lg p-4" data-testid="technical-specs">{p.teknik_ozellikler}</div>
            </section>
          )}

          {/* Spec table */}
          <section>
            <h2 className="font-head text-lg font-semibold text-slate-900 mb-3">Ürün Özellikleri</h2>
            <div className="bg-white border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  {specs.map(([k, v], i) => (
                    <tr key={k} className={i % 2 ? "bg-slate-50" : ""}>
                      <td className="px-4 py-2.5 text-slate-500 w-1/3 font-medium">{k}</td>
                      <td className="px-4 py-2.5 text-slate-900">{String(v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {p.diger_isimler && (
            <section>
              <h3 className="font-head font-semibold text-slate-900 mb-2 text-sm">Diğer İsimler / Arama Terimleri</h3>
              <div className="flex flex-wrap gap-1.5">
                {p.diger_isimler.split(";").map((n, i) => n.trim() && (
                  <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">{n.trim()}</span>
                ))}
              </div>
            </section>
          )}

          {(p.bilesim_bilgisi || p.urun_kullanim_uyarisi) && (
            <section className="space-y-3">
              {p.bilesim_bilgisi && <p className="text-sm text-slate-600"><b>Bileşim:</b> {p.bilesim_bilgisi}</p>}
              {p.urun_kullanim_uyarisi && <p className="text-sm text-slate-600"><b>Kullanım Uyarısı:</b> {p.urun_kullanim_uyarisi}</p>}
            </section>
          )}

          {d.iliskiler.length > 0 && (
            <section>
              <h3 className="font-head font-semibold text-slate-900 mb-2 text-sm">Sektör ve Kullanım Alanları</h3>
              <div className="space-y-2">
                {d.iliskiler.map((r, i) => (
                  <div key={i} className="text-sm bg-white border rounded-lg p-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-900">{r.sektor_adi_tr}</span>
                      <span className="text-slate-400">→</span>
                      <span className="text-slate-600">{r.kullanim_adi_tr}</span>
                      {r.rol_adi && <Badge variant="secondary" className="text-[10px]">{r.rol_adi}</Badge>}
                    </div>
                    {r.uygunluk_notu && <p className="text-xs text-slate-400 mt-1">{r.uygunluk_notu}</p>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {(d.uyumlu_cihazlar.length > 0 || d.opsiyonel_aksesuarlar.length > 0) && (
            <section className="grid sm:grid-cols-2 gap-4">
              {d.uyumlu_cihazlar.length > 0 && (
                <div>
                  <h3 className="font-head font-semibold text-slate-900 mb-2 text-sm flex items-center gap-1.5">
                    <Cpu className="w-4 h-4 text-teal-600" /> Uyumlu Cihazlar</h3>
                  {d.uyumlu_cihazlar.map((c, i) => (
                    <Link key={i} to={`/urun/${c.urun_id}`} className="block text-sm text-teal-700 hover:underline py-1">
                      {c.model || c.kod}</Link>
                  ))}
                </div>
              )}
              {d.opsiyonel_aksesuarlar.length > 0 && (
                <div>
                  <h3 className="font-head font-semibold text-slate-900 mb-2 text-sm flex items-center gap-1.5">
                    <Puzzle className="w-4 h-4 text-teal-600" /> Opsiyonel Aksesuarlar</h3>
                  {d.opsiyonel_aksesuarlar.map((a, i) => (
                    <Link key={i} to={`/urun/${a.urun_id}`} className="block text-sm text-teal-700 hover:underline py-1">
                      {a.model || a.kod}</Link>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>

        {/* Buy box */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-xl p-5 lg:sticky lg:top-20 shadow-sm">
            <h3 className="font-head font-semibold text-slate-900 flex items-center gap-2 mb-4">
              <Package className="w-4 h-4 text-teal-600" /> Teklif Listesine Ekle
            </h3>

            {variants.length > 0 && (
              <div className="mb-4">
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Ambalaj Seçimi</label>
                <div className="space-y-1.5">
                  {variants.map((v) => (
                    <button key={v.urun_id} data-testid={`variant-${v.urun_id}`}
                      onClick={() => selectVariant(v.urun_id)}
                      className={`w-full text-left text-sm px-3 py-2 rounded-lg border transition-colors ${selected === v.urun_id ? "border-teal-500 bg-teal-50 text-teal-800" : "border-slate-200 hover:border-slate-300"}`}>
                      <span className="flex justify-between gap-2">
                        <span>{v.ambalaj || `${v.ambalaj_miktari || ""} ${v.ambalaj_birimi || ""}`}</span>
                        <span className="font-mono text-xs text-slate-500" data-nosnippet>Ürün kodu: {v.urun_kodu}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Miktar (ambalaj adedi)</label>
            <div className="flex items-center gap-1 border rounded-lg w-fit mb-2">
              <button className="p-2 hover:bg-slate-100" onClick={() => setQty(Math.max(1, qty - 1))} data-testid="detail-dec-qty">
                <Minus className="w-4 h-4" /></button>
              <Input type="number" min={1} value={qty} data-testid="detail-qty-input"
                onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-16 h-9 text-center border-0" />
              <button className="p-2 hover:bg-slate-100" onClick={() => setQty(qty + 1)} data-testid="detail-inc-qty">
                <Plus className="w-4 h-4" /></button>
            </div>
            {selVariant.ambalaj_miktari && (
              <p className="text-xs text-teal-700 mb-4">
                {qty} × {selVariant.ambalaj_miktari} {selVariant.ambalaj_birimi} = toplam{" "}
                <b>{(selVariant.ambalaj_miktari * qty).toLocaleString("tr-TR")} {selVariant.ambalaj_birimi}</b>
              </p>
            )}

            <Button className="w-full bg-teal-600 hover:bg-teal-700 h-11" onClick={add} data-testid="detail-add-to-quote-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Teklif Listeme Ekle
            </Button>
            <p className="text-xs text-slate-400 mt-3 text-center">
              Fiyatlandırma talebiniz sonrası değerlendirilecektir.
            </p>
          </div>
        </div>
      </div>

      {/* Benzer ürünler */}
      {d.benzer_urunler?.length > 0 && (
        <section className="mt-12" data-testid="similar-products">
          <h2 className="font-head text-xl font-semibold text-slate-900 mb-4">Benzer Ürünler</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {d.benzer_urunler.map((b) => (
              <div key={b.urun_id} data-testid={`similar-${b.urun_id}`}
                className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col hover:border-teal-300 transition-colors">
                <div className="font-mono text-xs text-slate-400" data-nosnippet>Ürün kodu: {b.urun_kodu}</div>
                <Link to={productPath(b)}
                  className="font-medium text-sm text-slate-900 hover:text-teal-700 mt-1 leading-snug">
                  {b.urun_adi_tr}
                </Link>
                <div className="text-xs text-slate-600 mt-1 mb-3">
                  {b.marka}{b.ambalaj ? ` · ${b.ambalaj}` : ""}
                </div>
                <Button size="sm" variant="outline"
                  className="mt-auto h-8 border-teal-200 text-teal-700 hover:bg-teal-50 whitespace-nowrap"
                  onClick={() => { addItem(b); toast.success(`${b.urun_adi_tr} teklif sepetine eklendi`); }}
                  data-testid={`similar-add-${b.urun_id}`}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Teklif Sepetine Ekle
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
